/**
 * Whether a cloud account is ready to deploy into, step by step.
 *
 * Getting an account ready takes four things in the customer's own AWS account,
 * and until now they lived only in `docs/first-deployment-runbook.md` — a
 * document someone has to find, read and follow correctly. The control plane
 * already knows almost all of the answer, so the screen can say it.
 *
 * The state that made this obvious: the test account had a dispatcher deployed a
 * version behind, a grant that had expired three days earlier, and — per the
 * story — no registry credentials. Three independent reasons a run would fail,
 * none visible anywhere until someone read the stack by hand.
 *
 * ## Why this is a pure function over facts
 *
 * The reads happen in an adapter, against one cloud. What the facts *mean* is the
 * same everywhere and is the part worth testing exhaustively, so it lives here
 * with no SDK anywhere near it — and the console renders the answer rather than
 * deciding it, so the screen and the API cannot disagree about whether an account
 * is ready.
 *
 * ## Unknown is not done
 *
 * Every step is three-valued. A read that failed reports `unknown`, never `done`
 * — an account reported ready because a call timed out is worse than one
 * reported unreadable, because only the second sends someone to look.
 */
import { checkBootstrap } from '../bootstrap/compatibility.js';
import { CHOSEN_IMAGE_DISPATCHER_VERSION, REFRESHING_CREDENTIALS_DISPATCHER_VERSION, SESSION_TAG_DISPATCHER_VERSION, } from '../bootstrap/dispatcher-template.js';
import { installerChoiceOf } from '../deployment/run-installer.js';
import { RUN_CEILING_HOURS } from '../runs/execution.js';
import { SESSION_TAG_GRANT_VERSION } from '../bootstrap/grant-template.js';
import { DEPLOY_POLICY_ADMINISTRATOR, DEPLOY_POLICY_SCOPED, SCOPED_DEPLOY_BOOTSTRAP_VERSION, } from '../bootstrap/template.js';
import { UNVERSIONED_REDACTION } from '../bootstrap/redaction.js';
import { RUNNER_IMAGE_EXAMPLE, isRunnerImageAddress } from '../bootstrap/runner-image.js';
import { GRANT_WARNING_DAYS, describeDaysLeft, grantDaysLeft } from './grant.js';
/**
 * Compares two version numbers numerically, not as strings.
 *
 * Same reasoning as `checkBootstrap`: a string compare orders `'10'` before
 * `'2'`, a bug that appears only once the tenth release ships and then reports
 * every account as behind. An unparseable version compares as equal, so a
 * malformed value never renders as "out of date" — the step reads `unknown`
 * instead, decided by the caller.
 */
const compare = (a, b) => {
    const x = /^\d+$/.test(a.trim()) ? Number.parseInt(a.trim(), 10) : undefined;
    const y = /^\d+$/.test(b.trim()) ? Number.parseInt(b.trim(), 10) : undefined;
    if (x === undefined || y === undefined)
        return 0;
    return x - y;
};
/**
 * Whether a run started now would finish before the grant ends (IM-191).
 *
 * In a region whose dispatcher is v15 or later, a run's credentials are renewed
 * by a fresh `AssumeRole` about every hour, and each one is judged against the
 * grant's `aws:CurrentTime` condition. So a grant that lapses mid-run cuts the
 * run off at its next renewal — partway through an apply, leaving
 * infrastructure half-built in someone else's account. The run therefore has
 * to start with the whole run ceiling still inside the grant.
 *
 * Below v15 a run holds one hour of static keys, and a session issued inside
 * the window lives out that hour, so only "is it open now" matters there, as it
 * always did. A v15 region running an older installer image also gets static
 * keys, and is held to the margin anyway: which image a region runs is not a
 * fact this side can rely on, and over-refusing in the last six hours of a
 * grant costs far less than an apply cut in half.
 *
 * `undefined` when the expiry is absent or unparseable — the caller decides
 * what not knowing means, and nowhere here may it mean "yes".
 */
export const grantOutlastsRun = (grantExpiresAt, dispatcherVersion, now) => {
    if (grantExpiresAt === undefined)
        return undefined;
    const expires = new Date(grantExpiresAt).getTime();
    if (Number.isNaN(expires))
        return undefined;
    const refreshes = dispatcherVersion !== undefined &&
        /^\d+$/.test(dispatcherVersion.trim()) &&
        compare(dispatcherVersion, REFRESHING_CREDENTIALS_DISPATCHER_VERSION) >= 0;
    const marginMs = refreshes ? RUN_CEILING_HOURS * 3_600_000 : 0;
    return expires > now.getTime() + marginMs;
};
/**
 * Whether the deploy role refuses a session with no deployment tag (IM-50).
 *
 * Read from the access stack's own `DeployPolicy` output. A v8 stack whose
 * output could not be read counts as scoped — not the template's default,
 * which is `administrator` until MP-3630, but the direction that fails safe: the screen then asks for the grant and
 * dispatcher updates a scoped role needs, rather than promising a run that
 * would be refused.
 */
const deployRoleScoped = (facts) => {
    if (facts.deployPolicy !== undefined)
        return facts.deployPolicy !== DEPLOY_POLICY_ADMINISTRATOR;
    return (facts.bootstrapVersion !== undefined &&
        /^\d+$/.test(facts.bootstrapVersion.trim()) &&
        compare(facts.bootstrapVersion, SCOPED_DEPLOY_BOOTSTRAP_VERSION) >= 0);
};
/** A grant stack too old to let the runner tag its session. Absent is too old. */
const grantCannotTag = (facts) => compare(facts.grantVersion ?? '0', SESSION_TAG_GRANT_VERSION) < 0;
/** A dispatcher too old to tag its session. Only a version that was read counts. */
const dispatcherCannotTag = (facts) => facts.dispatcherVersion !== undefined &&
    compare(facts.dispatcherVersion, SESSION_TAG_DISPATCHER_VERSION) < 0;
/**
 * Whether the access stack lets a run start — which is not whether its step is
 * `done` (IM-50).
 *
 * The step shows attention for an access stack one release behind, and should:
 * somebody should know. But a stack behind and above the floor runs exactly as
 * before — `checkBootstrap` calls that `behind`, and `bootstrapPermitsRuns`
 * says yes — and reading the step's state here refused it, on the console and
 * in the preflight, for runs AWS would take. It was the IM-72 mistake for the
 * access stack, invisible while no account was behind, and v8 would have
 * turned every v7 account to "cannot run" on the day it shipped — the day the
 * grant and dispatcher have to be updated *before* the access stack, which a
 * blocked console cannot let anybody do in that order.
 */
const accessPermitsRuns = (facts, steps) => {
    const state = steps.find((s) => s.id === 'access-template')?.state;
    if (state === 'done')
        return true;
    if (state === 'unknown' || facts.bootstrapVersion === undefined)
        return false;
    return checkBootstrap(facts.bootstrapVersion).state === 'behind';
};
/** The steps, in the order someone performs them. */
export const ONBOARDING_STEPS = [
    'access-template',
    'region-stack',
    'registry-credentials',
    'dispatcher',
    'redaction',
    'grant',
    'completions',
];
/** Which of the two each step answers for. Exhaustive over `OnboardingStepId`. */
export const STEP_SCOPE = {
    'access-template': 'account',
    grant: 'account',
    'region-stack': 'region',
    dispatcher: 'region',
    redaction: 'region',
    completions: 'region',
    'registry-credentials': 'region',
};
const step = (id, title, state, detail, nextAction) => ({
    id,
    title,
    // Looked up rather than passed: a step's scope is a property of where its
    // resource lives, not something each call site should be able to disagree on.
    scope: STEP_SCOPE[id],
    state,
    detail,
    ...(nextAction === undefined ? {} : { nextAction }),
});
const accessTemplateStep = (facts, expect) => {
    if (facts.bootstrapUnreadable === true) {
        return step('access-template', 'Access template', 'unknown', '', 'Could not read the account. Check the diagnostics role exists and its external ID matches, then reload.');
    }
    if (facts.bootstrapVersion === undefined) {
        return step('access-template', 'Access template', 'attention', 'Not deployed', 'Send the customer the access template from this screen. It creates the diagnostics role, the deploy role and the Terraform state backend.');
    }
    // A newer bootstrap than this build is forward-compatible — it never removes
    // anything this control plane calls (IM-51) — so it is not a fault.
    if (compare(facts.bootstrapVersion, expect.bootstrapVersion) < 0) {
        /*
         * Named, not numbered.
         *
         * "v5 deployed, v6 expected" tells a customer nothing they can weigh, and
         * asking somebody to redeploy a security artifact deserves a reason. The
         * compatibility matrix exists precisely to answer "what would this gain
         * me" — it records what each release *added* — so the step reads it rather
         * than restating a number twice.
         *
         * This became load-bearing at v6 (IM-130), the first release whose gain a
         * customer might reasonably decline: it adds permission to read their AWS
         * cost totals, and they are entitled to know that is what they are
         * agreeing to.
         */
        const compatibility = checkBootstrap(facts.bootstrapVersion);
        const gains = compatibility.state === 'behind' || compatibility.state === 'too-old'
            ? compatibility.missing
            : [];
        return step('access-template', 'Access template', 'attention', `v${facts.bootstrapVersion} deployed, v${expect.bootstrapVersion} expected`, `Send the customer the updated access template from this screen and ask them to re-run their existing stack.${gains.length === 0 ? '' : ` It adds: ${gains.join(', ')}.`}`);
    }
    return step('access-template', 'Access template', 'done', `v${facts.bootstrapVersion} deployed${facts.deployPolicy === DEPLOY_POLICY_SCOPED
        ? ', deploy role scoped to one deployment per run'
        : facts.deployPolicy === DEPLOY_POLICY_ADMINISTRATOR
            ? ', deploy role has AdministratorAccess (chosen on the stack)'
            : ''}`);
};
const registryStep = (facts) => {
    if (facts.dispatcherUnreadable === true) {
        return step('registry-credentials', 'Registry credentials', 'unknown', '');
    }
    if (facts.registryCredentialsConfigured === true) {
        return step('registry-credentials', 'Registry credentials', 'done', 'A secret is configured');
    }
    if (facts.dispatcherVersion === undefined) {
        // Nothing to configure them on yet. Not this step's problem to report.
        return step('registry-credentials', 'Registry credentials', 'unknown', 'No dispatcher stack to read them from');
    }
    return step('registry-credentials', 'Registry credentials', 'attention', 'None configured', 'The runner cannot pull the installer image. Give the customer a registry token and have them set RegistryUser and RegistryKey on the dispatcher stack, or point RegistryCredentialsSecretArn at a secret they manage.');
};
/**
 * Whether the deployed `RunnerImage` is known not to be an image address.
 *
 * Only a value that was read and fails the shape counts. An absent one is
 * unknown rather than malformed — a stack that could not be read, or one read
 * without that parameter — and "unknown is not done" is not the same as
 * "unknown is fatal".
 */
const runnerImageMalformed = (facts) => facts.runnerImage !== undefined && !isRunnerImageAddress(facts.runnerImage);
const dispatcherStep = (facts, expect) => {
    if (facts.dispatcherUnreadable === true) {
        return step('dispatcher', 'Run dispatcher', 'unknown', '', 'Could not read the dispatcher stack. It may not exist, or the diagnostics role may not be able to describe it.');
    }
    if (facts.dispatcherVersion === undefined) {
        return step('dispatcher', 'Run dispatcher', 'attention', 'Not deployed', 'Download the dispatcher template from this screen and send it to the customer. Runs cannot start without it.');
    }
    /*
     * A RunnerImage that is not an image address (IM-124).
     *
     * Before the version check, and regardless of whether anything is
     * recommended: this is not drift, it is a run that cannot start. ECS resolves
     * a bare name against Docker Hub, so every run in this region dies at image
     * pull — and does so with a 401 that reads like a credentials fault. v12
     * refuses such a value at deploy; a stack from before that can still hold one,
     * which is how the staging customer came to.
     */
    if (runnerImageMalformed(facts)) {
        return step('dispatcher', 'Run dispatcher', 'attention', `v${facts.dispatcherVersion} deployed, RunnerImage is not an image address`, `This region's RunnerImage is ${String(facts.runnerImage)}, which is not a registry/repository:tag address, so every run here will fail at image pull. ${expect.runnerImage === undefined
            ? `Download the dispatcher template from this screen and ask the customer to update this region's stack with the full address Qrvey gave them, such as ${RUNNER_IMAGE_EXAMPLE}.`
            : `Download the dispatcher template from this screen — it prefills ${expect.runnerImage} — and ask the customer to update this region's stack.`}`);
    }
    /*
     * A scoped deploy role and a dispatcher that cannot tag its session (IM-50).
     *
     * Before the ordinary "behind", because it is not ordinary: every run in
     * this region would be refused at its first AWS call by
     * `DenyWithoutDeploymentTag`. Same action as behind — update the stack — but
     * a reason that says it is blocking, and `dispatcherRunnable` agrees.
     */
    if (deployRoleScoped(facts) && dispatcherCannotTag(facts)) {
        return step('dispatcher', 'Run dispatcher', 'attention', `v${facts.dispatcherVersion} deployed, v${expect.dispatcherVersion} expected`, `This account's deploy role is scoped to one deployment per run, which needs dispatcher v${SESSION_TAG_DISPATCHER_VERSION} or later to tag each run with its deployment id — every run in this region will be refused until then. Download the dispatcher template from this screen and ask the customer to update this region's stack.`);
    }
    if (compare(facts.dispatcherVersion, expect.dispatcherVersion) < 0) {
        /*
         * Below v16 every run here uses the stack's RunnerImage, and the run form
         * offers no installer (IM-197). Said, because "an older one may refuse
         * runs" is not what a v15 region is missing: it runs everything, it just
         * cannot run one instance on a hotfix installer while the rest stay put.
         */
        const choosing = compare(facts.dispatcherVersion, CHOSEN_IMAGE_DISPATCHER_VERSION) < 0
            ? ` Until then every run in this region uses the stack's RunnerImage; v${CHOSEN_IMAGE_DISPATCHER_VERSION} lets a run choose another installer build, such as a hotfix, without moving the rest of the region.`
            : '';
        return step('dispatcher', 'Run dispatcher', 'attention', `v${facts.dispatcherVersion} deployed, v${expect.dispatcherVersion} expected`, 
        // The template served with this step prefills the recommended image
        // (IM-123), so updating the stack picks up a newer installer on the way —
        // which is why a version behind outranks the image check below rather
        // than reporting both.
        `Download the dispatcher template from this screen and ask the customer to update their stack. v${expect.dispatcherVersion} is needed; an older one may refuse runs.${choosing}`);
    }
    /*
     * The customer pinned this region's installer (IM-197). Their decision in
     * their own stack, so it is said rather than flagged: the step stays done.
     */
    const pinned = `${installerChoiceOf(facts).kind === 'fixed' ? ', installer fixed by RunImageTag' : ''}${clusterReadsNote(facts)}`;
    /*
     * A dispatcher on a different installer image (IM-123).
     *
     * **Differs, not behind.** Tags look like `9.5-r-20260831-0793` and nothing
     * here can order two of them, so naming a direction would be a guess — and
     * an account deliberately pinned ahead of the recommendation is a real case,
     * not a fault.
     *
     * Never blocking, for the same reason a dispatcher one release behind is
     * not: an older installer runs, it simply runs something other than what
     * this environment recommends. `canRun` reads the version floor and this is
     * not it.
     *
     * Reported per region because that is where the fact lives — `RunnerImage` is
     * a parameter of the dispatcher stack, and an account adding a second region
     * months later commonly has two different ones.
     */
    if (expect.runnerImage !== undefined && facts.runnerImage !== undefined) {
        if (facts.runnerImage !== expect.runnerImage) {
            return step('dispatcher', 'Run dispatcher', 'attention', `v${facts.dispatcherVersion} deployed, running ${facts.runnerImage}`, `This region runs a different installer image from the one Qrvey recommends (${expect.runnerImage}). Runs still work${pinned === '' && installerChoiceOf(facts).kind === 'allowed' ? ', and a run can choose the recommended build on its own' : ''}. Download the dispatcher template from this screen — it prefills the recommended image — and ask the customer to update this region's stack.`);
        }
        return step('dispatcher', 'Run dispatcher', 'done', `v${facts.dispatcherVersion} deployed, on ${facts.runnerImage}${pinned}`);
    }
    return step('dispatcher', 'Run dispatcher', 'done', `v${facts.dispatcherVersion} deployed${pinned}`);
};
/**
 * Whether the customer has opened this region's clusters to Qrvey's
 * diagnostics (IM-204). Their choice in their own stack, said beside the
 * version the way a pinned installer is, never flagged: `off` is a perfectly
 * good answer. Nothing below v19 can be either, so nothing is said there.
 */
const clusterReadsNote = (facts) => {
    switch (facts.clusterDiagnostics) {
        case 'read-only':
            return ', cluster reads allowed (ClusterDiagnostics)';
        case 'off':
            return ', cluster reads off (ClusterDiagnostics)';
        default:
            return '';
    }
};
/**
 * Which rules mask this region's run logs, against the ones this build ships
 * (IM-84).
 *
 * Its own step rather than one more line under the dispatcher's, because it is
 * a different order of fact. A dispatcher a release behind is missing a
 * convenience; a redactor a rule behind writes a secret that rule would have
 * caught into the customer's own log group, in the clear, on every run. Read
 * together they would be one "attention" among several, which is how a
 * security gap reads as housekeeping.
 *
 * **Loud, never blocking.** Refusing to run does not un-log anything already
 * written, and the dispatcher floor is the one thing that decides whether a run
 * can start. Whether a security-critical rule should one day refuse runs is a
 * policy call IM-84 deliberately leaves open, and would be made here.
 *
 * A stack from before the output existed is placed by `UNVERSIONED_REDACTION`:
 * v5 to v10 carry v1 exactly, anything older is reported as older than v1.
 */
const redactionStep = (facts, expect) => {
    if (facts.dispatcherUnreadable === true || facts.dispatcherVersion === undefined) {
        // The dispatcher step already says why; there is nothing more to add here.
        return step('redaction', 'Log redaction', 'unknown', '');
    }
    const deployed = facts.redactionRulesVersion ??
        (compare(facts.dispatcherVersion, UNVERSIONED_REDACTION.fromDispatcherVersion) >= 0
            ? UNVERSIONED_REDACTION.rulesVersion
            : undefined);
    const update = `Download the dispatcher template from this screen and ask the customer to update this region's stack before the next run. Runs still start, but a secret one of the newer rules masks would be written to the customer's own log group unmasked.`;
    if (deployed === undefined) {
        return step('redaction', 'Log redaction', 'attention', `Rules older than v${UNVERSIONED_REDACTION.rulesVersion}, v${expect.redactionRulesVersion} expected`, update);
    }
    if (compare(deployed, expect.redactionRulesVersion) < 0) {
        return step('redaction', 'Log redaction', 'attention', `Rules v${deployed} deployed, v${expect.redactionRulesVersion} expected`, update);
    }
    return step('redaction', 'Log redaction', 'done', `Rules v${deployed}`);
};
/**
 * The grant, which is its own stack and its own question.
 *
 * Every branch here used to be decided by `dispatcherUnreadable` and
 * `dispatcherVersion`, which was correct while one stack held both and became
 * IM-99 the moment IM-94 split them: a healthy account with a perfectly good
 * grant reported "Not set" and `canRun: false`, because the code was asking a
 * stack that no longer knows the answer.
 */
const grantStep = (facts, expect, now) => {
    if (facts.grantUnreadable === true) {
        return step('grant', 'Access grant', 'unknown', '');
    }
    if (facts.grantExpiresAt === undefined) {
        return step('grant', 'Access grant', 'attention', 'No grant stack deployed', 
        // Not "set GrantExpiresAt on it": there is no stack to set it on, and
        // sending someone to edit a parameter on a stack that does not exist is
        // the failure this step is supposed to prevent.
        'Download the grant template from this screen and ask the customer to deploy it. Without it there is no runner role and no window in which runs may start.');
    }
    const expires = new Date(facts.grantExpiresAt);
    if (Number.isNaN(expires.getTime())) {
        return step('grant', 'Access grant', 'unknown', `Unreadable expiry: ${facts.grantExpiresAt}`, 'The stack reports a GrantExpiresAt this control plane cannot parse. It must be UTC, yyyy-mm-ddThh:mm:ss.');
    }
    const hoursLeft = (expires.getTime() - now.getTime()) / 3_600_000;
    const daysLeft = grantDaysLeft(facts.grantExpiresAt, now);
    if (hoursLeft <= 0) {
        return step('grant', 'Access grant', 'attention', `Expired ${facts.grantExpiresAt}${daysLeft === undefined ? '' : `, ${describeDaysLeft(daysLeft)}`}`, 
        // The one thing this control plane cannot do for them: the expiry is an
        // IAM condition in their account, so only their stack update moves it.
        'The grant has lapsed and every run will be refused, in every region. Download the grant template from this screen and ask the customer to update their grant stack with a new GrantExpiresAt, in UTC as yyyy-mm-ddThh:mm:ss.');
    }
    /*
     * Open, but inside the run ceiling (IM-191). Ranked above everything but a
     * lapsed grant: in any region on dispatcher v15 a new run is refused now,
     * and everywhere else it is hours from the same answer.
     */
    if (hoursLeft < RUN_CEILING_HOURS) {
        return step('grant', 'Access grant', 'attention', `Expires ${facts.grantExpiresAt}, in under ${String(RUN_CEILING_HOURS)} hours`, `A run may take up to ${String(RUN_CEILING_HOURS)} hours, and where the dispatcher is v${REFRESHING_CREDENTIALS_DISPATCHER_VERSION} or later its credentials are renewed under this grant — so a run started now could be cut off partway through an apply. New runs there are refused until the grant is extended. Download the grant template from this screen and ask the customer to update their grant stack with a later GrantExpiresAt, in UTC as yyyy-mm-ddThh:mm:ss.`);
    }
    /*
     * Behind ranks above the near-expiry warning, because the action subsumes it:
     * redeploying the grant stack is when a new expiry gets set anyway. It ranks
     * below a lapsed grant, which refuses every run right now.
     *
     * An absent version means a stack older than the output, which every grant
     * stack has carried since IM-94 — so absent is behind, not unknown, and both
     * want the same action.
     */
    /*
     * A scoped deploy role and a grant that cannot let the runner tag its session
     * (IM-50). Blocking, unlike the ordinary behind below: the runner's tagged
     * assume-role is refused, the untagged fallback is refused on every call, and
     * that is every run in every region.
     */
    if (deployRoleScoped(facts) && grantCannotTag(facts)) {
        return step('grant', 'Access grant', 'attention', facts.grantVersion === undefined
            ? `Version not reported, v${expect.grantVersion} expected`
            : `v${facts.grantVersion} deployed, v${expect.grantVersion} expected`, `This account's deploy role is scoped to one deployment per run, which needs grant v${SESSION_TAG_GRANT_VERSION} or later so the runner may tag each run with its deployment id — every run will be refused until then, in every region. Download the grant template from this screen and ask the customer to update their grant stack. Set GrantExpiresAt while they are there, in UTC as yyyy-mm-ddThh:mm:ss.`);
    }
    if (compare(facts.grantVersion ?? '0', expect.grantVersion) < 0) {
        return step('grant', 'Access grant', 'attention', facts.grantVersion === undefined
            ? `Version not reported, v${expect.grantVersion} expected`
            : `v${facts.grantVersion} deployed, v${expect.grantVersion} expected`, `Download the grant template from this screen and ask the customer to update their grant stack. v${expect.grantVersion} is needed. Set GrantExpiresAt while they are there, in UTC as yyyy-mm-ddThh:mm:ss.`);
    }
    /*
     * Fourteen days, where this used to be 24 hours.
     *
     * A day's notice on a window whose only fix is a CloudFormation stack update
     * in someone else's account arrives after it is useful: it reports the outage
     * rather than preventing it. Two weeks is enough for a chore somebody
     * schedules, and it is the same horizon the daily digest uses, so the two
     * surfaces cannot disagree about when this became a thing to do.
     *
     * It still never clears `canRun` — AWS will permit a run right up to the
     * instant — which is the same visible-versus-blocking split the dispatcher
     * version draws.
     */
    if (daysLeft !== undefined && daysLeft <= GRANT_WARNING_DAYS) {
        return step('grant', 'Access grant', 'attention', `Expires ${facts.grantExpiresAt}, ${describeDaysLeft(daysLeft)}`, expect.internalGrantDays === undefined
            ? 'The grant is approaching its end. A run in flight finishes, but a new one will be refused after it — ask the customer to extend GrantExpiresAt if more work is planned. Download the grant template from this screen.'
            : `The grant is approaching its end. Download the grant template from this screen and update this account's grant stack; the template already carries a new date ${String(expect.internalGrantDays)} days out.`);
    }
    /*
     * A long grant says out loud that it was chosen, because the alternative
     * reading is a typo. `internal` is what makes the difference visible rather
     * than quietly applied — the whole basis on which it is defensible.
     */
    return step('grant', 'Access grant', 'done', daysLeft === undefined
        ? `Open until ${facts.grantExpiresAt}`
        : `Open until ${facts.grantExpiresAt} (${String(daysLeft)} days)${expect.internalGrantDays === undefined
            ? ''
            : ` — internal account, granted ${String(expect.internalGrantDays)} days deliberately`}`);
};
/**
 * The region stack: the state bucket, the lock table and the run log group.
 *
 * Unchecked until 2026-09-18 even though the stack has always published
 * `/qrvey/region/version`. Nothing read it, so a region on an old stack read as
 * fully set up — and since this stack is what writes the version parameter the
 * access step used to trust, a stale region stack also made the *access* step
 * lie. Two symptoms, one unread fact.
 *
 * Behind is attention and never blocking: an older region stack still holds
 * state and still locks, and the dispatcher step is what reports a region that
 * cannot actually run anything.
 */
const regionStackStep = (facts, expect) => {
    if (facts.regionVersion === undefined) {
        /*
         * A region is discovered by its state bucket, so a listed region with no
         * version parameter means the bucket outlived its stack — the bucket is
         * `Retain`, deliberately, so deleting the stack leaves it behind forever.
         * That is a real and confusing state, and saying so beats reporting the
         * region as merely incomplete.
         */
        return step('region-stack', 'Region stack', 'attention', 'No region stack here, though a state bucket exists', 'The state bucket in this region has no region stack behind it — deleting that stack leaves the bucket, which is why the region still appears. Deploy the region template from this screen, or delete the bucket if the region is genuinely finished with.');
    }
    if (compare(facts.regionVersion, expect.regionVersion) < 0) {
        return step('region-stack', 'Region stack', 'attention', `v${facts.regionVersion} deployed, v${expect.regionVersion} expected`, `Download the region template from this screen and ask the customer to update this region's stack. v${expect.regionVersion} is needed.`);
    }
    return step('region-stack', 'Region stack', 'done', `v${facts.regionVersion} deployed`);
};
const completionsStep = (facts) => {
    if (facts.dispatcherUnreadable === true || facts.dispatcherVersion === undefined) {
        return step('completions', 'Run completion reporting', 'unknown', '');
    }
    if (facts.reportsCompletion === true) {
        return step('completions', 'Run completion reporting', 'done', 'Wired to Qrvey');
    }
    return step('completions', 'Run completion reporting', 'attention', 'Not wired', 'Runs will start but never report finishing, so they sit at running until they time out. Re-download the dispatcher template from this screen — that prefills QrveyEventBusArn — and have the customer update their stack.');
};
/**
 * The whole assessment.
 *
 * `canRun` is what someone about to click a button is asking, and it is not the
 * same as "onboarding complete": a lapsed grant refuses runs on an otherwise
 * perfect account, and a missing registry secret stops the image being pulled.
 * Nor is it "everything is current" — a dispatcher one additive release behind
 * shows as attention and still runs.
 * Completion reporting being unwired does not stop a run *starting*, so it does
 * not clear `canRun` — it is reported as attention because the run will look
 * stuck rather than fail.
 */
export const assessOnboarding = (facts, expect, now = new Date()) => {
    const steps = [
        accessTemplateStep(facts, expect),
        regionStackStep(facts, expect),
        registryStep(facts),
        dispatcherStep(facts, expect),
        redactionStep(facts, expect),
        grantStep(facts, expect, now),
        completionsStep(facts),
    ];
    /**
     * Being *visible* and being *blocking* are different questions.
     *
     * A grant with a week left renders as attention — someone planning work
     * needs to know — but AWS will permit a run, and reporting it as unrunnable
     * would be this console overriding the thing that actually decides. So the
     * grant contributes to `canRun` only when no run could finish inside it,
     * which is why this reads the fact rather than the step's state.
     *
     * "Could finish" is new with IM-191. Where a run's credentials renew under
     * the grant, AWS *will* permit it to start in the grant's last hours and then
     * cut it off at the next renewal, mid-apply. Refusing that start is not this
     * console overriding AWS; it is declining to begin something AWS has already
     * said it will stop.
     */
    const grantOpen = (() => {
        // `grantUnreadable`, not `dispatcherUnreadable` (IM-99). They are separate
        // stacks now and either can fail alone, so borrowing the dispatcher's flag
        // both missed an unreadable grant and blamed a readable one.
        if (facts.grantUnreadable === true)
            return false;
        // Open *for a whole run*, in a region whose credentials renew under it
        // (IM-191) — see `grantOutlastsRun`. Unknown is not open.
        return grantOutlastsRun(facts.grantExpiresAt, facts.dispatcherVersion, now) === true;
    })();
    /*
     * A grant can be open and still refuse every run: a scoped deploy role needs
     * the runner to tag its session, and a grant below v3 does not permit it
     * (IM-50). Kept apart from `grantOpen` because the two want different words
     * on the step, and the same answer here.
     */
    const grantUsable = grantOpen && !(deployRoleScoped(facts) && grantCannotTag(facts));
    /**
     * Whether the deployed dispatcher can actually be reached — the same
     * distinction as `grantOpen`, and it was got wrong the same way.
     *
     * The dispatcher step compares against the version this build *publishes*, so
     * an account one additive release behind renders as attention, which is
     * right: someone should know. But it will still run, and reading the step's
     * state here reported it as unrunnable (IM-72) — telling an engineer a run
     * would fail when it would not, and sending them to ask a customer to update
     * a stack that did not need updating. Only being below the floor genuinely
     * stops a run, so that is what this reads.
     *
     * A malformed version compares as equal and so counts as runnable, matching
     * `compare`'s existing stance: never report a value we cannot parse as out of
     * date, and let AWS give the real answer.
     */
    const dispatcherRunnable = facts.dispatcherUnreadable !== true &&
        facts.dispatcherVersion !== undefined &&
        compare(facts.dispatcherVersion, expect.minimumDispatcherVersion) >= 0 &&
        /*
         * A RunnerImage that is not an address clears it too (IM-124), and this is
         * the one image fact that may. A *different* valid image never does — an
         * older installer runs. But a bare tag is resolved against Docker Hub and
         * every run fails at image pull, so it is the same kind of fact as being
         * below the floor: not "behind", but "a run started now will not work".
         * Leaving it runnable would be the IM-72 mistake in the other direction —
         * the screen telling someone to click a button that is certain to fail.
         */
        !runnerImageMalformed(facts) &&
        // And, under a scoped deploy role, one that tags its session (IM-50).
        !(deployRoleScoped(facts) && dispatcherCannotTag(facts));
    const accessUsable = accessPermitsRuns(facts, steps);
    /**
     * Which step's problem is one that stops a run.
     *
     * The registry secret blocks until it is done. The others block on the fact
     * rather than on their step's state, for the reasons above: being visible
     * and being blocking are different questions.
     */
    const blocksRun = (s) => {
        if (s.id === 'registry-credentials')
            return s.state !== 'done';
        if (s.id === 'access-template')
            return !accessUsable;
        if (s.id === 'grant')
            return !grantUsable;
        if (s.id === 'dispatcher')
            return !dispatcherRunnable;
        return false;
    };
    /*
     * After whatever stops a run, a stale redactor comes before anything merely
     * behind (IM-84): the other steps cost someone a feature, and this one costs
     * a customer a secret in their logs. Both say to update the dispatcher
     * stack, so ordering them does not change what to do — it changes why.
     */
    return decide(steps.map((step) => ({ step, blocks: blocksRun(step) })), ['redaction']);
};
/**
 * The rule every cloud's assessment ends with, kept in one place (IM-45).
 *
 * Each cloud decides its own steps and which of them stop a run, because those
 * are facts about its own resources. What follows from that is the same
 * everywhere, and a second copy is where two clouds would come to disagree:
 *
 * - **`canRun` is "no step blocks".**
 * - **`blockedBy` is the blocking steps that are not done**, and `canRun` is
 *   exactly "this is empty" for every step a cloud only blocks while undone.
 * - **The headline belongs to a blocking step.** The headline action used to
 *   be whichever step came first in the list, and `dispatcher` sorts before
 *   `grant`. That was survivable only while most accounts were on the current
 *   dispatcher version — the moment one is published, *every* deployed
 *   account is behind, and "update your dispatcher stack" masks "your grant
 *   lapsed", which is the single most common reason a run is actually refused.
 *   Found by bumping to v6 for IM-80. After the blocking steps come those the
 *   cloud names in `ahead`, then the rest in order.
 */
export const decide = (verdicts, ahead = []) => {
    const steps = verdicts.map((v) => v.step);
    const withAction = verdicts.filter((v) => v.step.nextAction !== undefined);
    const next = withAction.find((v) => v.blocks) ??
        ahead.map((id) => withAction.find((v) => v.step.id === id)).find((v) => v !== undefined) ??
        withAction[0];
    return {
        steps,
        canRun: !verdicts.some((v) => v.blocks),
        blockedBy: verdicts.filter((v) => v.blocks && v.step.state !== 'done').map((v) => v.step.id),
        ...(next?.step.nextAction === undefined ? {} : { nextAction: next.step.nextAction }),
    };
};
/**
 * Assesses an account and each of its regions.
 *
 * Account-level facts are gathered once and region-level facts per region, so
 * the caller does not pay for the account's roles being re-read for every
 * region it has.
 *
 * A region whose facts could not be read still appears, with its steps
 * `unknown` — the same rule as everywhere here. **Unknown is not done**, and an
 * account ready in one region and unreadable in another is not "ready"; it is
 * ready in one place, which is exactly what this shape says.
 */
export const assessAccount = (account, byRegion, expect, now = new Date()) => {
    const accountWhole = assessOnboarding(account, expect, now);
    const accountSteps = accountWhole.steps.filter((s) => s.scope === 'account');
    const accountBlockedBy = accountWhole.blockedBy.filter((id) => STEP_SCOPE[id] === 'account');
    /*
     * The account's own steps gate every region: no roles, no runs anywhere.
     *
     * Except the grant, which is weighed below by `whole.canRun` through
     * `grantOpen` — the rule that tells "closing soon" from "closed". Requiring
     * its step to be `done` here counted it a second time, more strictly: a grant
     * inside its fourteen-day warning, or a grant stack one version behind, reads
     * `attention`, and that turned every region to "cannot run" for runs AWS
     * would permit (IM-162). The step still shows attention; it no longer
     * blocks.
     */
    const accountReady = accessPermitsRuns(account, accountSteps) &&
        accountSteps.every((s) => s.id === 'grant' || s.id === 'access-template' || s.state === 'done');
    const regions = Object.keys(byRegion)
        .sort()
        .map((region) => {
        const facts = byRegion[region] ?? {};
        /*
         * The region's own facts, plus **only** the grant.
         *
         * This was `{ ...account, ...facts }`, and that was a false positive
         * generator. The account-level read is itself a read of one region — the
         * control plane's own — and `readOnboardingFacts` *omits* a fact it could
         * not find rather than setting it undefined. So a region with no
         * dispatcher had no `dispatcherVersion` key to override the account
         * spread's, and silently inherited the dispatcher from us-east-1:
         * `done, v6 deployed`, `canRun: true`, for a region where no run can be
         * dispatched at all.
         *
         * Found on a real account that had a region stack in `us-east-2` and
         * `us-west-2` and a dispatcher in neither. The console reported all three
         * regions ready.
         *
         * The grant is the one thing genuinely account-wide (IM-94) — one grant
         * covering every region — and every region's `canRun` depends on it, so
         * it is carried across explicitly. Dispatcher, completions and registry
         * credentials are per region by definition: they are exactly the steps
         * `STEP_SCOPE` marks `region`, and inheriting them is inheriting the
         * answer to the question being asked.
         */
        const withGrant = {
            ...facts,
            ...(account.grantExpiresAt === undefined ? {} : { grantExpiresAt: account.grantExpiresAt }),
            /*
             * And what the account's own stacks say about tagging (IM-50): the
             * access stack's `DeployPolicy` and the grant's version. Both are
             * account-wide, and together they decide whether a dispatcher in this
             * region that cannot tag its session is merely behind or blocking.
             */
            ...(account.deployPolicy === undefined ? {} : { deployPolicy: account.deployPolicy }),
            ...(account.grantVersion === undefined ? {} : { grantVersion: account.grantVersion }),
            ...(account.grantUnreadable === undefined
                ? {}
                : { grantUnreadable: account.grantUnreadable }),
        };
        const whole = assessOnboarding(withGrant, expect, now);
        const steps = whole.steps.filter((s) => s.scope === 'region');
        // Same rule as `assessOnboarding`: the blocking problem is the headline.
        // `whole` carries every step with its state, so the ranking is reused
        // rather than re-derived from a narrower list.
        const ranked = whole.steps.filter((s) => s.nextAction !== undefined);
        const visible = new Set([...accountSteps, ...steps].map((s) => s.id));
        const next = ranked.find((s) => visible.has(s.id) && s.nextAction === whole.nextAction) ??
            ranked.find((s) => visible.has(s.id));
        return {
            region,
            steps,
            // `whole.canRun` already weighs the grant and the dispatcher floor; the
            // account gate is re-applied because those steps were filtered out of
            // this region's list and would otherwise go unchecked.
            canRun: accountReady && whole.canRun,
            suppliesRegistryKey: withGrant.registryCredentialsConfigured === true &&
                withGrant.dispatcherVersion !== undefined &&
                compare(withGrant.dispatcherVersion, expect.registryFromAccountVersion) >= 0,
            blockedBy: whole.blockedBy.filter((id) => STEP_SCOPE[id] === 'region'),
            ...(facts.runnerImage === undefined ? {} : { runnerImage: facts.runnerImage }),
            installerChoice: installerChoiceOf(facts),
            ...(next?.nextAction === undefined ? {} : { nextAction: next.nextAction }),
        };
    });
    return {
        accountSteps,
        accountBlockedBy,
        regions,
        canRunSomewhere: regions.some((r) => r.canRun),
    };
};
//# sourceMappingURL=onboarding.js.map